import { computed, makeAutoObservable, toJS } from "mobx"
import moment from "moment";

import {
	DISPATCHABLE_STATUSES, InvoiceDetail, InvoiceModel,
	Invoice, InvoicePostSplit, InvoiceStatus, modelToInvoice,
	getInvoiceTotal, InvoiceImport, InvoiceAttachment, PaymentType,
} from '../models/Invoice';
import { getInvoiceDifferences } from '../models/InvoiceChanges';
import { categoryStore } from './CategoryStore';
import {
	addInvoice, deleteInvoice, getInvoices, updateInvoice, updateStatus,
	getInvoice, emailChangesToAuthor, getStatusHistory, getSplitInvoices,
	processInvoices, attachFileToInvoice, moveFile, findAttachFileByName,
	getInvoicePaymentRequests,
} from '../services/InvoicesAPI';
import { getAttachedFileData } from "../services/SharepointAPI";
import {
	detectInvisibleCharacters, formatDate, formatValidDate,
	isGuid, showDialog, showFormDialog, showInfoToast
} from '../services/shared';
import { dateStore } from './DateStore';
import { currentUserStore } from './CurrentUser';
import {
	isAccountActive, isCategoryActive, isCustomerActive, isLocationActive,
	isSplitActive, loadReferenceData, FETCH_MESSAGE, isVendorActive,
} from './Common';
import { notifyStakeHolders } from '../services/UsersAPI';
import { errorStore } from './ErrorStore';
import { validateBusinessDate } from '../services/DateValidator';
import { getFiles } from "../services/SharepointAPI";
import { FileTreeNode, SharePointProcessFileResult } from "../models/SharePoint";
import { invoicePaymentTypeStore } from "./InvoicePaymentTypeStore";
import { PaymentRequestInvoice, PaymentRequestResult } from "../models/PaymentRequest";

class InvoiceStore {
	data: Invoice[] = [];
	importData: InvoiceImport[] = [];
	ImportFileNames: string[] = [];
	showModal: boolean = false;
	isBusy: boolean = false;
	originalRow: Invoice | undefined;
	currentRow: InvoiceModel | undefined;
	postSplitData: InvoicePostSplit[] = [];
	noRowsMessage: string = FETCH_MESSAGE;
	viewMode: 'PreSplit' | 'PostSplit' = 'PreSplit';
	sharePointFiles: FileTreeNode[] = [];

	constructor() {
		makeAutoObservable(this);
	}

	async loadData(fromDate: moment.Moment, toDate: moment.Moment) {
		if (this.isBusy) return;

		try {
			const fromDateStr = formatValidDate(fromDate.toISOString());
			const toDateStr = formatValidDate(toDate.toISOString());
			this.setIsBusy(true);
			await loadReferenceData();
			const response = await getInvoices(fromDateStr, toDateStr);
			this.setData(response || []);
			if (this.data.length === 0) {
				this.setNoRowsMessage(`No invoices found between ${fromDate.format('MM/DD/YYYY')} and ${toDate.format('MM/DD/YYYY')}`);
			}
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async loadInvoice(invoiceId: string) {
		if (this.isBusy) return;

		try {
			this.setIsBusy(true);

			await loadReferenceData();
			const response = await getInvoice(invoiceId);
			if (response) {
				this.setCurrentRow(new InvoiceModel(response));
				this.setOriginalRow(response);
				this.setShowModal(true);
				return response;
			}
			this.setError('Invoice not found.');
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async refreshInvoice(invoiceId: string) {
		if (this.isBusy) return;

		try {
			this.setIsBusy(true);
			const response = await getInvoice(invoiceId);
			if (response) {
				this.setData(this.data.map(invoice => invoice.id === invoiceId ? response : invoice));
			} else {
				this.setError('Invoice not found.');
			}
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async loadPostSplitData(fromDate: moment.Moment, toDate: moment.Moment) {
		if (this.isBusy) return;

		try {
			const fromDateStr = formatValidDate(fromDate.toISOString());
			const toDateStr = formatValidDate(toDate.toISOString());
			if (this.isBusy) return;
			this.setIsBusy(true);

			await loadReferenceData();
			const response = await getSplitInvoices(fromDateStr, toDateStr);
			this.setPostSplitData(response || []);
			if (this.postSplitData.length === 0) {
				this.setNoRowsMessage(`No invoices found between ${fromDate.format('MM/DD/YYYY')} and ${toDate.format('MM/DD/YYYY')}`);
			}
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async loadSharePointFiles() {
		if (this.isBusy) return;

		try {
			this.setIsBusy(true);
			const response = await getFiles();
			this.setSharePointFiles(response || []);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	private createAttachmentUpdate(
		invoiceId: string,
		response: SharePointProcessFileResult,
		existingAttachment?: InvoiceAttachment,
	) {
		return {
			attachmentUrl: response.fileUrl,
			attachment: {
				id: existingAttachment?.id ?? 0,
				invoiceId,
				driveId: response.driveId,
				fileId: response.fileId,
				fileName: response.fileName,
				fileUrl: response.fileUrl,
			},
		};
	}

	private handleAttachment(invoiceId: string, response: SharePointProcessFileResult, dataType?: 'InvoiceImport' | 'Invoice') {
		if (dataType === 'InvoiceImport') {
			this.setImportData(this.importData.map((invoice) => invoice.id === invoiceId
				? { ...invoice, ...this.createAttachmentUpdate(invoiceId, response, invoice.attachment) }
				: invoice
			));
		} else if (dataType === 'Invoice') {
			this.setData(this.data.map((invoice) => invoice.id === invoiceId
				? { ...invoice, ...this.createAttachmentUpdate(invoiceId, response, invoice.attachment) }
				: invoice
			));
		}

		if (this.currentRow?.id === invoiceId) {
			this.currentRow.update(this.createAttachmentUpdate(invoiceId, response, this.currentRow.attachment));
		}
	}

	async findAttachment(invoiceId: string, fileName: string) {
		if (this.isBusy) return;
		try {
			this.setIsBusy(true);
			const response = await findAttachFileByName(fileName);
			this.handleAttachment(invoiceId, response, 'InvoiceImport');
			return response.fileUrl;
		} catch (error: any) {
			this.setError(this.getAttachFileErrorMessage(error));
		} finally {
			this.setIsBusy(false);
		}
	}

	async attachFile(invoiceId: string, fileId: string) {
		if (this.isBusy) return;
		try {
			this.setIsBusy(true);
			const response = await attachFileToInvoice(invoiceId, fileId);
			this.handleAttachment(invoiceId, response, 'Invoice')
			return response.fileUrl;
		} catch (error: any) {
			this.setError(this.getAttachFileErrorMessage(error));
		} finally {
			this.setIsBusy(false);
		}
	}

	async getFileDetails(fileId: string) {
		if (this.isBusy) return;
		try {
			this.setIsBusy(true);
			const response = await getAttachedFileData(fileId);
			if (!this.currentRow) return;

			this.handleAttachment(this.currentRow.id, response);
		}
		catch (error: any) {
			this.setError("Failed to fetch attachment details");
		} finally {
			this.setIsBusy(false);
		}
	}

	async moveAttachment(invoiceId: string) {
		if (this.isBusy) return;

		try {
			this.setIsBusy(true);
			await this.moveAttachmentOrThrow(invoiceId);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async loadPaymentRequests(invoice: PaymentRequestInvoice, forceRefresh = false) {
		if (this.isBusy) return;
		if (!forceRefresh && invoice.paymentRequests !== undefined) return;

		try {
			this.setIsBusy(true);
			const results = await getInvoicePaymentRequests(invoice.id);
			this.setPaymentRequests(invoice, results);
		} catch (error) {
			this.setPaymentRequests(invoice, undefined);
			this.setError(error instanceof Error ? error.message : 'Unable to load payment requests.');
		} finally {
			this.setIsBusy(false);
		}
	}

	private async moveAttachmentOrThrow(invoiceId: string) {
		try {
			await moveFile(invoiceId);
		} catch (error: any) {
			throw new Error(this.getAttachFileErrorMessage(error));
		}
	}

	shouldMoveAttachment(invoice: Invoice) {
		const selectedAttachmentFileId = invoice.attachment?.fileId;
		if (!selectedAttachmentFileId) return false;
		if (this.originalRow?.id !== invoice.id) return true;

		return selectedAttachmentFileId !== this.originalRow.attachment?.fileId;
	}

	private getAttachFileErrorMessage = (error: any) => {
		const message = error?.message;
		if (!message || typeof message !== 'string') {
			return 'Unable to attach the selected file.';
		}

		try {
			const response = JSON.parse(message) as Partial<SharePointProcessFileResult>;
			return response.message || message;
		} catch {
			return message;
		}
	}

	private validateTextForInvisibleChars = (value: string, fieldLabel: string, errors: string[]) => {
		const invisibleChars = detectInvisibleCharacters(value);
		if (invisibleChars.length > 0) {
			errors.push(`${fieldLabel} contains invisible characters that may cause API errors: ${invisibleChars.join(', ')}`);
		}
	}

	private addDateValidationError = (dateValue: string | Date, field: { label: string }, errors: string[]) => {
		const result = validateBusinessDate(dateValue);
		if (!result.isValid) {
			errors.push(`${field.label}: ${result.errorMessage}`);
		}
	}

	private validate = (invoice: Invoice) => {
		const invoiceErrors: string[] = [];

		const invoiceFields = [
			{ key: 'vendorId', label: 'Vendor' },
			{ key: 'invoiceDate', label: 'Invoice Date' },
			{ key: 'postingPeriodDate', label: 'Posting Period Date' },
			{ key: 'invoiceNumber', label: 'Invoice Number' },
			{ key: 'dueDate', label: 'Due Date' },
			{ key: 'paymentType', label: 'Payment Method' },
		] as { key: keyof Invoice, label: string }[];

		invoiceFields.forEach(field => {
			if (field.key === 'invoiceNumber' && invoice.paymentType === PaymentType.ExternalCustomerInvoice) return;
			if (!invoice[field.key] || invoice[field.key] === '') {
				invoiceErrors.push(`${field.label} is required.`);
			}
		});

		if (invoice.vendorId && !isVendorActive(invoice.vendorId)) {
			invoiceErrors.push('Select an active vendor');
		}

		// Validate critical text fields for invisible characters
		if (invoice.vendorAccountNumber) {
			this.validateTextForInvisibleChars(invoice.vendorAccountNumber, 'Vendor Account Number', invoiceErrors);
		}
		if (invoice.invoiceNumber) {
			this.validateTextForInvisibleChars(invoice.invoiceNumber, 'Invoice Number', invoiceErrors);
		}
		if (invoice.contractNumber) {
			this.validateTextForInvisibleChars(invoice.contractNumber, 'Contract Number', invoiceErrors);
		}
		if (invoice.notes) {
			this.validateTextForInvisibleChars(invoice.notes, 'Notes', invoiceErrors);
		}

		type InvoiceDateField = 'invoiceDate' | 'dueDate' | 'postingPeriodDate';
		const invoiceDateFields: { key: InvoiceDateField, label: string }[] = [
			{ key: 'invoiceDate', label: 'Invoice Date' },
			{ key: 'postingPeriodDate', label: 'Posting Period Date' },
			{ key: 'dueDate', label: 'Due Date' },
		];

		// Validate invoice-level date fields
		invoiceDateFields.forEach(field => {
			const dateValue = invoice[field.key];
			if (dateValue) {
				this.addDateValidationError(dateValue, field, invoiceErrors);
			}
		});

		// For NetSuite: due date cannot be earlier than invoice date
		if (invoice.dueDate && invoice.invoiceDate) {
			const dueDate = new Date(invoice.dueDate);
			const invoiceDate = new Date(invoice.invoiceDate);

			if (isNaN(dueDate.getTime()) || isNaN(invoiceDate.getTime())) {
				invoiceErrors.push('Invalid date format for Invoice Date or Due Date');
			} else if (dueDate < invoiceDate) {
				invoiceErrors.push('Due Date cannot be earlier than Invoice Date');
			}
		}

		const invoiceTotal = getInvoiceTotal(invoice, 0);
		if (invoiceTotal === 0) {
			invoiceErrors.push('Invoice total cannot be $0.00 (negative amounts are allowed for credits/refunds)');
		}

		const detailFields = [
			{ key: 'detail', label: 'Description' },
			{ key: 'categoryId', label: 'Category' },
		] as { key: keyof InvoiceDetail, label: string }[];

		// Date fields to validate for invoice details
		type InvoiceDetailDateField = 'serviceFromDate' | 'serviceTillDate';
		const invoiceDetailDateFields: { key: InvoiceDetailDateField, label: string }[] = [
			{ key: 'serviceFromDate', label: 'Service From Date' },
			{ key: 'serviceTillDate', label: 'Service Till Date' },
		];

		// Detail-level checks
		invoice.invoiceDetails?.forEach((detail, index) => {
			const detailErrors: string[] = [];

			if (invoiceTotal === 0 && detail.amount === 0) {
				detailErrors.push(`Amount is required`);
			}

			detailFields.forEach(field => {
				const value = detail[field.key];
				if (!value || value === '' || value === 0) {
					detailErrors.push(`${field.label} is required`);
				}
			});

			// Validate detail-level date fields
			invoiceDetailDateFields.forEach(field => {
				const dateValue = detail[field.key];
				if (dateValue) {
					this.addDateValidationError(dateValue, field, detailErrors);
				}
			});

			if (detail.serviceFromDate && detail.serviceTillDate) {
				const serviceFromDate = new Date(detail.serviceFromDate);
				const serviceTillDate = new Date(detail.serviceTillDate);

				if (isNaN(serviceFromDate.getTime()) || isNaN(serviceTillDate.getTime())) {
					detailErrors.push('Invalid date format for Service From or Service Till');
				} else if (serviceTillDate < serviceFromDate) {
					detailErrors.push('Service Till cannot be earlier than Service From');
				}
			}

			// Validate text fields for invisible characters
			if (detail.detail) {
				this.validateTextForInvisibleChars(detail.detail, `Detail ${index + 1} Description`, detailErrors);
			}

			const hasSplit = !!detail.splitId;
			const hasAccount = !!detail.accountId;

			if (!hasSplit && !hasAccount) {
				detailErrors.push('Account or Split is required');
			}
			if (hasSplit && hasAccount) {
				detailErrors.push('Only Account or Split should be set, not both');
			}
			if (detail.categoryId && !isCategoryActive(detail.categoryId)) {
				detailErrors.push('Select an active category');
			}
			if (detail.splitId && !isSplitActive(detail.splitId)) {
				detailErrors.push('Select an active split');
			}
			if (detail.accountId && !isAccountActive(detail.accountId)) {
				detailErrors.push('Select an active account');
			}
			if (detail.customerId && !isCustomerActive(detail.customerId)) {
				detailErrors.push('Select an active customer');
			}
			if (detail.locationId && !isLocationActive(detail.locationId)) {
				detailErrors.push('Select an active location');
			}
			if (detailErrors.length > 0) {
				invoiceErrors.push(`${invoiceErrors.length === 0 ? '' : '\n'}Detail ${index + 1}: ${detailErrors.join(', ')}.`);
			}
		});

		if (invoiceErrors.length > 0) {
			throw new Error(invoiceErrors.join(' '));
		}
	}

	private prepareForSaving = (invoice: Invoice) => {
		// convert object from Models to underlying objects
		const invoiceToSave = { ...invoice } as Invoice;
		for (const key in invoiceToSave) {
			const invoiceKey = key as keyof Invoice;
			if (!invoice[invoiceKey] || invoiceToSave[invoiceKey] === '') {
				delete (invoiceToSave as any)[key];
			}
		}

		invoiceToSave.invoiceDetails = invoiceToSave.invoiceDetails?.map(detail => ({
			...detail as InvoiceDetail,
		}));

		for (const detail of invoiceToSave.invoiceDetails ?? []) {
			for (const key in detail) {
				const detailKey = key as keyof InvoiceDetail;
				if (!detail[detailKey] || detail[detailKey] === '') {
					delete (detail as any)[key];
				}
			}
		}
		invoiceToSave.history = undefined;
		invoiceToSave.paymentRequests = undefined;

		return {
			...invoiceToSave,
			departmentId: invoice.departmentId,
			invoiceTotal: getInvoiceTotal(invoice),
			receivedDate: formatValidDate(invoice.receivedDate),
			invoiceDate: formatValidDate(invoice.invoiceDate),
			postingPeriodDate: formatValidDate(invoice.postingPeriodDate),
			dueDate: formatValidDate(invoice.dueDate),
			status: isGuid(invoice.id) ? InvoiceStatus.Edited : InvoiceStatus.Created,
			statusDate: new Date().toISOString(),
			actor: currentUserStore.userId,
			attachmentUrl: invoice.attachmentUrl?.trim(),
			notes: invoice.notes?.trim(),
			invoiceDetails: invoiceToSave.invoiceDetails?.map(detailRow => ({
				...detailRow as InvoiceDetail,
				categoryMarkup: categoryStore.data.find(cat => cat.id === detailRow.categoryId)?.markup ?? 0,
				detail: detailRow.detail.trim(),
				serviceFromDate: formatDate(detailRow.serviceFromDate),
				serviceTillDate: formatDate(detailRow.serviceTillDate),
			}))
		}
	}

	async addNew(invoice: Invoice) {
		try {
			this.validate(invoice);
			this.setIsBusy(true);
			const preppedInvoice = this.prepareForSaving(invoice);
			const response = await addInvoice(preppedInvoice);
			if (response) {
				this.setCurrentRow(new InvoiceModel(response));
				// if the entry date in the date range displayed, add it to the data and grid
				if (moment(response.receivedDate).isBetween(dateStore.fromDate, dateStore.toDate, undefined, '[]')) {
					this.setData([response, ...this.data]);
				}
				if (this.importData.length > 0) {
					this.setImportData(this.importData.map(imp => imp.vendorId === invoice.vendorId && imp.invoiceNumber === invoice.invoiceNumber
						? { ...imp, ...response } : imp
					));
				}
				this.setMessage('Invoice added successfully.');
			}
			return response;
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async update(invoice: Invoice) {
		try {
			this.setIsBusy(true);
			const response = await this.updateOrThrow(invoice);
			this.setMessage('Invoice updated successfully.');
			return response;
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async approveInvoice(id: string) {
		await this.updateInvoiceStatus(id, InvoiceStatus.Approved);
	}

	private async updateOrThrow(invoice: Invoice) {
		this.validate(invoice);

		const preppedInvoice = this.prepareForSaving(invoice);
		const response = await updateInvoice(preppedInvoice);

		if (!response) {
			throw new Error('Invoice update failed.');
		}

		const updatedInvoice = this.mergeInvoiceResponse(invoice, response);
		this.setData(this.data.map((inv) => inv.id === invoice.id ? updatedInvoice : inv));
		this.setCurrentRow(new InvoiceModel(updatedInvoice));

		return updatedInvoice;
	}

	private mergeInvoiceResponse(invoice: Invoice, response: Partial<Invoice>): Invoice {
		return {
			...invoice,
			...response,
			attachment: response.attachment ?? invoice.attachment,
			attachmentUrl: response.attachmentUrl ?? invoice.attachmentUrl,
			history: response.history ?? invoice.history,
			invoiceDetails: response.invoiceDetails ?? invoice.invoiceDetails,
		};
	}

	async saveAndApproveInvoice() {
		const invoice = this.currentRow ? modelToInvoice(this.currentRow) : undefined;
		if (!invoice) return;

		this.setIsBusy(true);

		try {
			const shouldMoveAttachment = this.shouldMoveAttachment(invoice);
			const updatedInvoice = await this.updateOrThrow(invoice);

			if (shouldMoveAttachment) {
				await this.moveAttachmentOrThrow(updatedInvoice.id);
			}

			const requests: Promise<unknown>[] = [
				this.approveInvoice(updatedInvoice.id),
			];

			// send any changes to the prev author if the approver is different from the previous author
			// and originalRow id is same as the one being saved (precautionary check)
			if (this.originalRow && this.originalRow.actor !== currentUserStore.userId && this.originalRow.id === invoice.id) {
				const changesToSend = getInvoiceDifferences(this.originalRow, invoice);
				if (changesToSend) {
					changesToSend.approverId = currentUserStore.userId;
					changesToSend.authorId = this.originalRow.actor;
					// Only send origin + pathname, strip query params and hash for security
					changesToSend.url = `${window.location.origin}${window.location.pathname}`;

					requests.push(emailChangesToAuthor(changesToSend));
				}
			}

			await Promise.all(requests);
		} catch (error: any) {
			this.setError(error.message);
			throw new Error(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async confirmAndDeny(id: string) {
		const denyInvoice = async (reason: string) => {
			await this.updateInvoiceStatus(id, InvoiceStatus.Denied, reason.trim());
		};

		showFormDialog((reason) => denyInvoice(reason));
	};

	async confirmAndReset(id: string) {
		showDialog(
			'Are you sure you want to reset this invoice?',
			async () => await this.updateInvoiceStatus(id, InvoiceStatus.Reversed)
		);
	};

	async updateInvoiceStatus(id: string, status: InvoiceStatus, reason?: string) {
		try {
			const invoice = this.currentRow?.id === id ? modelToInvoice(this.currentRow) : this.data.find(inv => inv.id === id);
			if (invoice && currentUserStore.isAuthenticated) {
				this.setIsBusy(true);

				const response = await updateStatus({
					invoiceId: id,
					status: status,
					statusReason: reason,
					statusDate: new Date().toISOString(),
					actor: currentUserStore.userId,
				});

				if (response) {
					const newStatus = {
						invoiceId: response.id,
						actor: response.actor,
						status: response.status,
						statusDate: response.statusDate,
						statusReason: response.statusReason,
					}

					const updatedInvoice = this.mergeInvoiceResponse(invoice, response);
					this.setData(this.data.map((inv) => inv.id === id ? updatedInvoice : inv));

					if (this.currentRow?.id === id) {
						const { invoiceId, ...statusUpdate } = newStatus;
						this.currentRow.update({
							...statusUpdate,
							history: [newStatus, ...this.currentRow.history || []]
						});
					}
				}
			} else {
				this.setError('Invoice or user not found');
			}
		} catch (error: any) {
			this.setError(error.message);
			throw new Error(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async getStatusHistory(id: string, invoice: InvoiceModel) {
		try {
			this.setIsBusy(true);
			const response = await getStatusHistory(id);
			invoice.update({ 'history': response || [] });
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async delete(id: string) {
		try {
			this.setIsBusy(true);
			await deleteInvoice(id);
			this.setData(this.data.filter(inv => inv.id !== id));
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async notify() {
		try {
			if (this.isBusy) return;
			this.setIsBusy(true);

			const response = await notifyStakeHolders();
			if (response) {
				this.setMessage(response);
			}
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
		}
	}

	async dispatchInvoices(invoiceId?: string) {
		if (this.isBusy) return;

		try {
			this.setIsBusy(true);

			const response = await processInvoices(invoiceId);
			if (response) this.setMessage(Object.values(response).filter(Boolean).join('\n'));
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setIsBusy(false);
			if (invoiceId) {
				await this.refreshInvoice(invoiceId);
			} else {
				await this.loadData(dateStore.fromDate, dateStore.toDate);
			}
		}
	}

	@computed
	get dispatchableCount() {
		return this.data.filter(row => DISPATCHABLE_STATUSES.includes(row.status)).length;
	}

	@computed
	get paymentMethods() {
		return invoicePaymentTypeStore.options;
	}

	setIsBusy(loading: boolean) {
		this.isBusy = loading;
	}
	setPaymentRequests(invoice: PaymentRequestInvoice, paymentRequests: PaymentRequestResult[] | undefined) {
		invoice.paymentRequests = paymentRequests;
		const storedInvoice = this.data.find(inv => inv.id === invoice.id);
		if (storedInvoice) storedInvoice.paymentRequests = paymentRequests;
		if (this.currentRow?.id === invoice.id) this.currentRow.paymentRequests = paymentRequests;
	}
	setError(error: string) {
		errorStore.report(error);
	}
	setMessage(message: string) {
		showInfoToast(message);
	}
	setData(data: Invoice[]) {
		data.forEach(invoice =>
			invoice.invoiceTotal = getInvoiceTotal(invoice, 0) || 0
		);
		data.forEach(invoice =>
			invoice.invoiceDetails?.forEach((detail, index) => detail.lineItem = index + 1)
		);
		this.data = data;
	}
	setPostSplitData(data: InvoicePostSplit[]) {
		this.postSplitData = data;
	}
	setOriginalRow(invoice: Invoice) {
		this.originalRow = toJS(invoice) as Invoice;
	}
	clearOriginalRow() {
		this.originalRow = undefined;
	}
	setCurrentRow(row: InvoiceModel) {
		this.currentRow = row;
	}
	setShowModal(show: boolean) {
		this.showModal = show;
	}
	setNoRowsMessage(message: string) {
		this.noRowsMessage = message;
	}
	setViewMode(mode: 'PreSplit' | 'PostSplit') {
		this.viewMode = mode;
	}
	setSharePointFiles(files: FileTreeNode[]) {
		this.sharePointFiles = files;
	}
	resetStoreData() {
		this.data = [];
		this.postSplitData = [];
	}
	resetForDepartmentChange() {
		this.data = [];
		this.postSplitData = [];
		this.importData = [];
		this.ImportFileNames = [];
		this.sharePointFiles = [];
		this.originalRow = undefined;
		this.currentRow = undefined;
		this.showModal = false;
		this.noRowsMessage = FETCH_MESSAGE;
	}
	restoreDefaults() {
		this.noRowsMessage = FETCH_MESSAGE;
	}
	addFileName(name: string) {
		if (!this.ImportFileNames.includes(name)) {
			this.ImportFileNames.push(name);
		}
	}
	clearImportData() {
		this.importData = [];
		this.ImportFileNames = [];
	}
	setImportData(data: InvoiceImport[]) {
		data.forEach(invoice =>
			invoice.invoiceTotal = getInvoiceTotal(invoice, 0) || 0
		);
		this.importData = data;
	}
}

export const invoiceStore = new InvoiceStore();
